'use client';

import { useMemo } from 'react';
import { calculateLocalDiscount, isLocallyCalculable, type SalesRuleActionType } from '@kidslabs/module-quote';
import {
    evaluateVoucherConditions,
    type VoucherEvaluationContext,
    type ConditionEvaluationResult,
    type MagentoCondition,
} from '../utils';
import type { SalesRule } from '../../../types/front.graphql';

/**
 * Represents a locally-evaluated coupon with eligibility and estimated discount.
 */
export interface LocalEvaluatedCoupon {
    /** The original SalesRule coupon data */
    coupon: SalesRule;
    /** Whether the coupon is eligible based on local condition evaluation */
    isLocallyEligible: boolean;
    /** Detailed evaluation result with unmet conditions */
    evaluationResult: ConditionEvaluationResult;
    /** Estimated discount amount (VND, integer). 0 if not calculable locally. */
    estimatedDiscount: number;
    /** Whether the estimated discount is exact or approximate */
    isDiscountExact: boolean;
    /**
     * Confidence level:
     * - 'exact-local': Both conditions and discount evaluated locally with high confidence
     * - 'estimated': Conditions evaluated locally but discount is approximate
     * - 'conditions-only': Conditions evaluated locally, discount requires server
     * - 'server-required': Cannot evaluate locally (no rule data)
     */
    confidence: 'exact-local' | 'estimated' | 'conditions-only' | 'server-required';
}

/**
 * Rule data from availableCartPriceRules query.
 * Matched with coupons by rule_id or name for condition + discount evaluation.
 */
export interface AvailableRuleData {
    rule_id?: number | null;
    name?: string | null;
    simple_action?: string | null;
    discount_amount?: number | null;
    discount_qty?: number | null;
    discount_step?: number | null;
    max_discount_amount?: number | null;
    conditions?: MagentoCondition[] | null;
}

/**
 * Local-first coupon evaluation hook.
 *
 * Evaluates coupon eligibility and estimates discount amounts entirely on the client,
 * using the condition evaluator and local discount calculator.
 *
 * Flow:
 * 1. Match each coupon with its corresponding rule from availableCartPriceRules
 * 2. Evaluate rule conditions against current cart context
 * 3. Estimate discount amount based on rule simple_action + discount_amount
 * 4. Return evaluated coupons with eligibility + estimated savings
 *
 * Server validation (validateCoupons) still runs in parallel for:
 * - Usage limit checks (requires DB state)
 * - Complex conditions not supported locally
 * - Final confirmation before apply
 *
 * @param coupons - Available coupon list from availableCoupons query
 * @param rules - Available rule data from availableCartPriceRules query
 * @param evaluationContext - Cart + customer context for condition evaluation
 */
export const useLocalCouponEvaluation = (
    coupons: SalesRule[],
    rules: AvailableRuleData[],
    evaluationContext: VoucherEvaluationContext
): {
    evaluatedCoupons: LocalEvaluatedCoupon[];
    locallyValidCoupons: LocalEvaluatedCoupon[];
    locallyInvalidCoupons: LocalEvaluatedCoupon[];
} => {
    const evaluatedCoupons = useMemo((): LocalEvaluatedCoupon[] => {
        if (!coupons.length) return [];

        return coupons.map(coupon => {
            // Try to match coupon with its rule data
            const matchedRule = findMatchingRule(coupon, rules);

            if (!matchedRule || !matchedRule.conditions?.length) {
                // No rule data or no conditions — cannot evaluate locally
                return {
                    coupon,
                    isLocallyEligible: true, // Assume eligible, let server decide
                    evaluationResult: { isEligible: true, unmetConditions: [] },
                    estimatedDiscount: 0,
                    isDiscountExact: false,
                    confidence: 'server-required' as const,
                };
            }

            // Step 1: Evaluate conditions locally
            const rootCondition: MagentoCondition = {
                type: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Combine',
                value: '1',
                aggregator: 'all',
                conditions: matchedRule.conditions,
            };
            const evaluationResult = evaluateVoucherConditions(rootCondition, evaluationContext);

            // Step 2: Estimate discount if conditions pass
            let estimatedDiscount = 0;
            let isDiscountExact = false;
            let confidence: LocalEvaluatedCoupon['confidence'] = 'conditions-only';

            if (evaluationResult.isEligible && matchedRule.simple_action && matchedRule.discount_amount) {
                if (isLocallyCalculable(matchedRule.simple_action)) {
                    const discountResult = calculateLocalDiscount({
                        simpleAction: matchedRule.simple_action as SalesRuleActionType,
                        discountAmount: matchedRule.discount_amount,
                        cartSubtotal: evaluationContext.cartSubtotal,
                        cartItems: [], // Simplified — subtotal-based calculation
                        maxDiscountAmount: matchedRule.max_discount_amount ?? undefined,
                    });
                    estimatedDiscount = discountResult.estimatedDiscount;
                    isDiscountExact = discountResult.isExact;
                    confidence = discountResult.isExact ? 'exact-local' : 'estimated';
                }
            }

            return {
                coupon,
                isLocallyEligible: evaluationResult.isEligible,
                evaluationResult,
                estimatedDiscount,
                isDiscountExact,
                confidence,
            };
        });
    }, [coupons, rules, evaluationContext]);

    const { locallyValidCoupons, locallyInvalidCoupons } = useMemo(() => {
        const valid = evaluatedCoupons.filter(ec => ec.isLocallyEligible);
        const invalid = evaluatedCoupons.filter(ec => !ec.isLocallyEligible);
        return { locallyValidCoupons: valid, locallyInvalidCoupons: invalid };
    }, [evaluatedCoupons]);

    return {
        evaluatedCoupons,
        locallyValidCoupons,
        locallyInvalidCoupons,
    };
};

/**
 * Find the matching rule for a coupon by name matching.
 *
 * Magento's availableCoupons returns SalesRule with name/code,
 * and availableCartPriceRules returns Rule with name/rule_id.
 * We match by name since coupon code is not exposed on the Rule type.
 */
const findMatchingRule = (coupon: SalesRule, rules: AvailableRuleData[]): AvailableRuleData | undefined => {
    if (!rules.length) return undefined;

    // Match by rule name (most reliable cross-reference)
    if (coupon.name) {
        const byName = rules.find(r => r.name === coupon.name);
        if (byName) return byName;
    }

    return undefined;
};
